"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createMemoDeriveForm = exports.deriveFormMemoLast = void 0;
exports.deriveFormFromFormModel = deriveFormFromFormModel;
const derive_form_1 = require("../modules/derive-form");
const selectors_1 = require("../modules/selectors");
const composure_1 = require("@icg360/composure");
const validators_1 = require("../utils/validators");
const pi_syntax_tools_1 = require("@icg360/pi-syntax-tools");
const createMemoLastDeriveForm = () => {
    let deriveFormCache = null;
    let deriveFormArgs = [];
    return (props) => {
        if (props.version === 1) {
            const { groups, models, fusions, formId, formIndex } = props;
            const args = [models, fusions, formId, formIndex];
            if (deriveFormCache &&
                args.every((arg, i) => arg === deriveFormArgs[i])) {
                return deriveFormCache;
            }
            deriveFormCache = (0, derive_form_1.deriveForm)(groups, models, fusions);
            deriveFormArgs = args;
            return deriveFormCache;
        }
        const { formState, formLayout, formId, stage, fusions } = props;
        const args = [formState, fusions, formId, stage];
        if (deriveFormCache && args.every((arg, i) => arg === deriveFormArgs[i])) {
            return deriveFormCache;
        }
        deriveFormCache = deriveFormFromFormModel(formState, formLayout, stage, fusions);
        deriveFormArgs = args;
        return deriveFormCache;
    };
};
exports.deriveFormMemoLast = createMemoLastDeriveForm();
// external use
const blankReturn = [];
const createMemoDeriveForm = () => {
    const memo = createMemoLastDeriveForm();
    return (state, props = {}) => {
        const { id, index, stage } = (0, selectors_1.useFormOrCurrent)(state, props);
        if (!state.rex.forms[id])
            return blankReturn;
        if (state.rex.version[id] > 1) {
            return memo({
                version: 2,
                formLayout: state.rex.composure[id].layout,
                formState: state.rex.formModel[id],
                formId: id,
                fusions: props.fusions,
                stage,
            });
        }
        const { groups } = state.rex.forms[id][index];
        if (!groups)
            return blankReturn;
        const models = state.rex.models[id];
        const { fusions } = props;
        return memo({
            version: 1,
            groups,
            models,
            fusions,
            formId: id,
            formIndex: index,
        });
    };
};
exports.createMemoDeriveForm = createMemoDeriveForm;
function sectionTitleToString(title, formState, iterableName, iterableIndex) {
    // Backward compatibility for a transition period
    if (typeof title === 'string') {
        return title;
    }
    if (title.type === 'text') {
        return title.value;
    }
    if (title.type === 'syntax') {
        return '';
    }
    const output = (0, pi_syntax_tools_1.execSyntax)(title.tree, (0, pi_syntax_tools_1.getContext)({
        ...formState,
        this: iterableName && iterableIndex !== undefined
            ? {
                iterable: formState.iterables[iterableName],
                index: iterableIndex,
                position: iterableIndex + 1,
            }
            : {},
    }));
    return String(output);
}
function deriveFormFromFormModel(formState, formLayout, stageIndex, fusions) {
    const stage = formLayout.stages[stageIndex];
    let heading = '';
    return stage.items.flatMap((item, index) => {
        if (item.type === 'heading') {
            heading = item.text.toLowerCase();
            return [];
        }
        if (item.type === 'section') {
            return [
                {
                    label: sectionTitleToString(item.title, formState),
                    ...(heading ? { section: heading } : null),
                    visible: true,
                    inFusion: false,
                    inputs: deriveGroupInputsFromFormModel(formState, item.items, { inFusion: false, positionContext: [index] }, fusions),
                },
            ];
        }
        if (item.type === 'super-repeat') {
            const sections = [];
            const count = formState.iterables[item.iterable]?.count || 0;
            for (let x = 0; x < count; x += 1) {
                for (const repeatItem of item.items) {
                    if (repeatItem.type === 'heading') {
                        // Don't support headings in super-repeats for now
                        continue;
                    }
                    sections.push({
                        label: sectionTitleToString(repeatItem.title, formState, item.iterable, x),
                        navLabel: repeatItem.altTitle &&
                            sectionTitleToString(repeatItem.altTitle, formState, item.iterable, x),
                        visible: true,
                        inFusion: false,
                        inputs: deriveGroupInputsFromFormModel(formState, repeatItem.items, {
                            inFusion: false,
                            positionContext: [index, x],
                            repeat: { id: item.iterable, index: x },
                        }, fusions),
                        repeat: {
                            id: item.iterable,
                            index: x,
                            labelForRemoveAction: item.labelForRemoveAction,
                        },
                    });
                }
            }
            return sections;
        }
        return [];
    });
}
function deriveGroupInputsFromFormModel(formState, items, inheritedContext, fusions) {
    if (!items)
        return [];
    return items.flatMap((item, index) => {
        const context = {
            ...inheritedContext,
            inFusion: false,
            positionContext: [...inheritedContext.positionContext, index],
        };
        if (item.type === 'input' || item.type === 'display') {
            return deriveFieldFromFormModel(formState, item, context);
        }
        if (item.type === 'control') {
            return deriveButtonFromFormModel(formState, item, context);
        }
        if (item.type === 'content') {
            return deriveContentFromFormModel(formState, item, context);
        }
        if (item.type === 'repeat') {
            const count = formState.iterables[item.iterable]?.count || 0;
            const inputs = [];
            for (let x = 0; x < count; x += 1) {
                inputs.push(deriveRepeatInputsFromFormModel(formState, item.items, {
                    ...context,
                    positionContext: [...context.positionContext, x],
                    repeat: {
                        id: item.iterable,
                        index: x,
                    },
                }, fusions));
            }
            return [
                {
                    type: 'repeat',
                    id: item.iterable,
                    count,
                    modelRef: fakeModelRef,
                    // TODO indent
                    inputs,
                },
            ];
        }
        if (item.type === 'fusion') {
            const fusion = mapFusionTypes(item.name);
            const isFusion = !!fusions?.[fusion];
            const group = {
                type: 'group',
                fusion,
                // TODO: indent
                // indent?: number | null;
                visible: true,
                inFusion: false,
                inputs: item.items.flatMap((item, index) => {
                    const newContext = {
                        ...context,
                        inFusion: isFusion,
                        fusionName: mapFusionName(item.name, fusion),
                        positionContext: [...context.positionContext, index],
                    };
                    if (item.type === 'input' || item.type === 'display') {
                        return deriveFieldFromFormModel(formState, item, newContext);
                    }
                    if (item.type === 'control') {
                        return deriveButtonFromFormModel(formState, item, newContext);
                    }
                    return [];
                }),
            };
            return [group];
        }
        return [];
    });
}
function deriveRepeatInputsFromFormModel(formState, items, inheritedContext, fusions) {
    if (!items)
        return [];
    return items.flatMap((item, index) => {
        const context = {
            ...inheritedContext,
            positionContext: [...inheritedContext.positionContext, index],
        };
        if (item.type === 'input' || item.type === 'display') {
            return deriveFieldFromFormModel(formState, item, context);
        }
        if (item.type === 'control') {
            return deriveButtonFromFormModel(formState, item, context);
        }
        if (item.type === 'content') {
            return deriveContentFromFormModel(formState, item, context);
        }
        if (item.type === 'fusion') {
            const fusion = mapFusionTypes(item.name);
            const isFusion = !!fusions?.[fusion];
            const group = {
                type: 'group',
                fusion,
                // TODO: indent
                // indent?: number | null;
                visible: true,
                inFusion: false,
                inputs: item.items.flatMap((item, index) => {
                    const newContext = {
                        ...context,
                        inFusion: isFusion,
                        fusionName: mapFusionName(item.name, fusion),
                        positionContext: [...context.positionContext, index],
                    };
                    if (item.type === 'input' || item.type === 'display') {
                        return deriveFieldFromFormModel(formState, item, newContext);
                    }
                    if (item.type === 'control') {
                        return deriveButtonFromFormModel(formState, item, newContext);
                    }
                    return [];
                }),
            };
            return [group];
        }
        throw new Error('Unknown item type' + item.type);
    });
}
const fakeModelRef = {};
function deriveFieldFromFormModel(formState, item, { inFusion, fusionName, repeat }) {
    const term = repeat
        ? formState.iterables[repeat.id]?.instances[repeat.index]?.terms[item.term]
        : formState.terms[item.term];
    if (!inFusion && !term?.isRelevant) {
        return [];
    }
    const type = item.type === 'display'
        ? 'output'
        : mapInputTypeToRexInputType({
            type: term.type,
            style: item.style,
            hasEnums: !!term.enums,
            isDateRange: term.meta?.isDateRange === 'true',
            hasSeparator: !!term.separator,
        });
    const label = renderLabel(item.label, term.label);
    const hint = item.type === 'display' ? undefined : item.help?.value;
    const help = item.type === 'display' ? undefined : item.help?.value;
    return [
        {
            type,
            isReadOnly: term.isReadonly,
            visible: term.isRelevant,
            value: term.value,
            initialValue: term.initial,
            onReadyValue: term.ready,
            placeholder: item.type === 'input' ? item.placeholder : undefined,
            pristine: term.value === term.ready,
            valid: !term.isRequired && term.isValid,
            isRequired: term.isRequired,
            ...deriveRangeFromFormModel(term),
            validationErrors: term.validation.flatMap((v) => v.pass ? [] : [mapValidationObject(v, term.validation)]),
            details: term.enums?.filter((e) => e.isRelevant),
            label,
            id: repeat ? `${repeat.id}_${repeat.index}_${item.term}` : item.term,
            inFusion,
            ...(repeat ? { repeatIndex: repeat.index + 1 } : null),
            ...(fusionName ? { fusionName } : null),
            // TODO indent
            modelRef: fakeModelRef,
            ...(hint !== undefined ? { hint } : null),
            ...(help !== undefined ? { help } : null),
            ...(item.style?.includes('radios') ? { appearance: 'full' } : null),
            ...(typeof term.meta?.rulesets === 'string'
                ? { rulesets: term.meta.rulesets.split(',') }
                : null),
        },
    ];
}
const noValidationErrors = [];
function deriveButtonFromFormModel(formState, item, { inFusion, fusionName, repeat, positionContext }) {
    let type;
    if (item.actions) {
        type = 'button';
    }
    if (item.submission) {
        type = 'submit';
    }
    if (!type) {
        throw new Error('Unknown control type');
    }
    if (!item.term) {
        const label = renderLabel(item.label);
        return [
            {
                type,
                modelRef: fakeModelRef,
                submission: item.submission ?? '',
                ...(item.actions
                    ? {
                        dispatch: item.actions.map((actionName) => ({
                            event: 'DOMActivate',
                            bubbles: 'true',
                            name: actionName,
                            target: actionName,
                        })),
                    }
                    : undefined),
                label,
                isReadOnly: false,
                visible: true,
                value: valueFromArgs(item.args, item.argsFn, formState, repeat),
                initialValue: '',
                onReadyValue: '',
                pristine: true,
                valid: true,
                isRequired: false,
                validationErrors: noValidationErrors,
                id: `REX_${positionContext.join('_')}`,
                inFusion,
                fusionName,
                repeatIndex: repeat ? repeat.index + 1 : undefined,
            },
        ];
    }
    const term = repeat
        ? formState.iterables[repeat.id]?.instances[repeat.index]?.terms[item.term]
        : formState.terms[item.term];
    if (!inFusion && !term?.isRelevant) {
        return [];
    }
    return [
        {
            type,
            modelRef: fakeModelRef,
            submission: item.submission ?? '',
            ...(item.actions
                ? {
                    dispatch: item.actions.map((actionName) => ({
                        event: 'DOMActivate',
                        bubbles: 'true',
                        name: actionName,
                        target: actionName,
                    })),
                }
                : null),
            label: renderLabel(item.label, term.label),
            isReadOnly: term.isReadonly,
            visible: term.isRelevant,
            value: term.value,
            initialValue: term.initial,
            onReadyValue: term.ready,
            pristine: term.value === term.ready,
            valid: term.isValid,
            isRequired: term.isRequired,
            validationErrors: term.validation.flatMap((v) => v.pass ? [] : [mapValidationObject(v, term.validation)]),
            details: term.enums?.filter((e) => e.isRelevant),
            id: repeat
                ? `${repeat.id}_${repeat.index}_control_${item.term}`
                : 'control_' + item.term,
            inFusion,
            repeatIndex: repeat ? repeat.index + 1 : undefined,
            fusionName,
            // TODO indent
        },
    ];
}
function deriveContentFromFormModel(formState, item, { inFusion, fusionName, repeat, positionContext }) {
    const contextForExec = (0, pi_syntax_tools_1.getContext)(repeat
        ? {
            ...formState,
            this: {
                iterable: formState.iterables[repeat.id],
                terms: formState.iterables[repeat.id]?.instances[repeat.index]?.terms,
                index: repeat.index,
                position: repeat.index + 1,
            },
        }
        : formState);
    const isRelevant = item.relevant?.type === 'syntax-tree'
        ? Boolean((0, pi_syntax_tools_1.execSyntax)(item.relevant.tree, contextForExec))
        : true;
    if (!inFusion && !isRelevant) {
        return [];
    }
    const baseOutput = {
        type: 'output',
        mediatype: 'text/plain',
        style: item.style,
        isReadOnly: true,
        value: '',
        initialValue: '',
        onReadyValue: '',
        pristine: true,
        isRequired: false,
        validationErrors: noValidationErrors,
        id: `REX_${positionContext.join('_')}`,
        valid: true,
        label: '',
        visible: isRelevant,
        modelRef: fakeModelRef,
        bindIndex: undefined,
        bind: undefined,
        indent: undefined,
        inFusion,
        fusionName: fusionName,
    };
    if (item.value.type === 'html') {
        return [
            {
                ...baseOutput,
                mediatype: 'text/html',
                value: item.value.value,
            },
        ];
    }
    if (item.value.type === 'syntax-tree') {
        const text = String((0, pi_syntax_tools_1.execSyntax)(item.value.tree, contextForExec));
        return [
            {
                ...baseOutput,
                value: text,
            },
        ];
    }
    if (item.value.type === 'text') {
        return [
            {
                ...baseOutput,
                value: item.value.value,
            },
        ];
    }
    return [];
}
function mapInputTypeToRexInputType({ type, style, hasEnums, isDateRange, hasSeparator, }) {
    if (style?.includes('checkbox')) {
        return 'boolean';
    }
    if (hasEnums) {
        return hasSeparator ? 'enum-multiple' : 'enum';
    }
    switch (type) {
        case 'string':
        case 'email':
        case 'url':
        case 'zipcode':
            return type;
        case 'integer':
        case 'decimal':
        case 'number':
            return 'number';
        case 'date':
            return isDateRange ? 'range' : 'date';
        case 'currency-integer':
        case 'currency-double':
        case 'currency':
            return 'currency';
        case 'tel':
            return 'phone';
        default:
            return 'string';
    }
}
function mapValidationObject(validation, validations) {
    switch (validation.type) {
        case 'length-min':
            return {
                type: 'minlength',
                val: validation.value,
                message: validators_1.validationMessages.minlength(validation.value)[0],
            };
        case 'length-max':
            return {
                type: 'maxlength',
                val: validation.value,
                message: validators_1.validationMessages.maxlength(validation.value)[0],
            };
        case 'amount-min':
            return {
                type: 'minvalue',
                val: validation.value,
                message: validators_1.validationMessages.minvalue(validation.value)[0],
            };
        case 'amount-max':
            return {
                type: 'maxvalue',
                val: validation.value,
                message: validators_1.validationMessages.maxvalue(validation.value)[0],
            };
        case 'date-range-min':
            return {
                type: 'range',
                val: undefined,
                message: `Minimum date is ${validation.value}. Maximum date is ${validations.find((v) => v.type === 'date-range-max')?.value}.`,
            };
        case 'date-range-max':
            return {
                type: 'range',
                val: undefined,
                message: `Minimum date is ${validations.find((v) => v.type === 'date-range-min')?.value}. Maximum date is ${validation.value}.`,
            };
        case 'value-type':
            if (validation.value === 'email') {
                return {
                    type: 'email',
                    val: undefined,
                    message: validators_1.validationMessages.email()[0],
                };
            }
            if (validation.value === 'tel') {
                return {
                    type: 'phone',
                    val: undefined,
                    message: validators_1.validationMessages.phone()[0],
                };
            }
            if (validation.value === 'url') {
                return {
                    type: 'url',
                    val: undefined,
                    message: validators_1.validationMessages.url()[0],
                };
            }
        case 'enum-option':
            return {
                type: 'enum-option',
                val: undefined,
                message: (0, composure_1.printValidationMessage)(validation),
            };
        default:
            throw new Error('Unknown validation type');
    }
}
function valueFromArgs(args, argsFn, formState, repeat) {
    const values = [];
    for (const arg of args) {
        values.push(arg.value);
    }
    if (argsFn?.type === 'syntax-tree') {
        const output = (0, pi_syntax_tools_1.execSyntax)(argsFn.tree, (0, pi_syntax_tools_1.getContext)({
            ...formState,
            this: {
                index: repeat?.index,
                position: repeat && repeat.index + 1,
            },
        }));
        if (output instanceof Map) {
            for (const value of output.values()) {
                values.push(value);
            }
        }
    }
    return values.join(',');
}
function mapFusionTypes(name) {
    if (name === 'mortgagee') {
        return 'mortgageeSearch';
    }
    return name;
}
function mapFusionName(name, fusion) {
    if (name === 'street-number') {
        return 'streetNumber';
    }
    if (name === 'street-name') {
        return 'streetName';
    }
    if (name === 'address-line2') {
        return 'addressLine2';
    }
    if (name === 'zipcode-plus4') {
        return 'zipcodePlus4';
    }
    if (name === 'address-line1') {
        return 'addressLine1';
    }
    if (name === 'address-line2') {
        return 'addressLine2';
    }
    if (fusion === 'mortgageeSearch' && name === 'zipcode') {
        return 'zip';
    }
    return name;
}
const undefinedRanges = {
    start: undefined,
    end: undefined,
};
function deriveRangeFromFormModel(term) {
    if (term.type !== 'date') {
        return undefinedRanges;
    }
    const dateRangeMin = term.validation?.find((v) => v.type === 'date-range-min');
    const dateRangeMax = term.validation?.find((v) => v.type === 'date-range-max');
    if (!dateRangeMin?.value || !dateRangeMax?.value)
        return undefinedRanges;
    return {
        start: String(dateRangeMin.value),
        end: String(dateRangeMax.value),
    };
}
function renderLabel(label, termLabel) {
    if (label?.type === 'syntax-tree' || label?.type === 'syntax') {
        console.warn('syntax-tree label not supported');
    }
    if (label?.type === 'text') {
        return label.value;
    }
    return termLabel || '';
}
//# sourceMappingURL=derive-form.js.map